import React from 'react';
import styled from '@emotion/styled';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
    ButtonGroup,
    Button,
    BodyTextMedium,
    HeadingText40,
    pxToRem,
} from '@modernhealth/vital-components';

import { config } from 'src/config/config';
import { toastNotificationsService } from 'src/config/rootService';
import { Dispatch } from 'src/config/rootStore';
import { CoachingVisitNode } from 'src/generated/webapp_gql';

import { useCancelCoachingSessionMutation } from '../documents/cancelCoachingSessionMutation.generated';
import { useAnalytics } from '@modernhealth/web-analytics';
import { EventLexicon } from '@modernhealth/analytics-lexicon';

interface SessionCancellationPromptContentProps {
    session: Pick<CoachingVisitNode, 'uuid'>;
    onSuccess: () => void;
    onError?: () => void;
    onCancel: () => void;
}

export const SessionCancellationPromptContent = ({
    session,
    onCancel,
    onError,
    onSuccess,
}: SessionCancellationPromptContentProps) => {
    const { t } = useTranslation();
    const analytics = useAnalytics();
    const { videoSessions, user } = useDispatch<Dispatch>();
    const [cancelSession, { loading: isCancellingSession }] = useCancelCoachingSessionMutation();

    const handleSessionCancel = async () => {
        analytics.trackEvent(
            EventLexicon.clickCancelSessionConfirmation({
                visitUuid: session.uuid,
            }),
        );

        try {
            const response = await cancelSession({ variables: { sessionUuid: session.uuid } });
            const ok = response.data?.cancelEvent?.ok;

            if (!ok) {
                throw new Error();
            }

            onSuccess();

            await videoSessions.fetchSessions();
            // Re-fetch user info since we're getting session utilization info from `me` query
            await user.fetchUserInfo();

            toastNotificationsService.renderSuccessToast(config.messages.sessionCanceled);
        } catch (err) {
            toastNotificationsService.renderErrorToast(config.messages.genericError);
            onError?.();
        }
    };

    return (
        <div>
            <CancelConfirmationContent>
                <HeadingText40 centered>
                    {t('videoSessions:session_details.cancel_confirmation.title')}
                </HeadingText40>
                <BodyTextMedium fontColor="gray65" centered>
                    <Trans
                        t={t}
                        ns="videoSessions"
                        i18nKey="session_details.cancel_confirmation.description"
                        components={{
                            strong: (
                                <HightlightText
                                    onClick={() =>
                                        window.open(
                                            'https://support.modernhealth.com/hc/en-us/articles/360001419846-Cancellation-Policy',
                                            '_blank',
                                        )
                                    }
                                />
                            ),
                        }}
                    />
                </BodyTextMedium>
            </CancelConfirmationContent>
            <CancelConfirmationButtonGroup>
                <ButtonGroup>
                    <Button variant="secondary" busy={isCancellingSession} onClick={onCancel}>
                        {t('common.back')}
                    </Button>
                    <Button
                        variant="destructive"
                        busy={isCancellingSession}
                        disabled={isCancellingSession}
                        onClick={handleSessionCancel}
                    >
                        {t('videoSessions:session_details.cancel')}
                    </Button>
                </ButtonGroup>
            </CancelConfirmationButtonGroup>
        </div>
    );
};

const CancelConfirmationContent = styled.div({
    maxWidth: '80%',
    margin: '0 auto',
});

const HightlightText = styled.span({
    fontWeight: 'bold',
    textDecoration: 'underline',
    cursor: 'pointer',
});

const CancelConfirmationButtonGroup = styled.div`
    margin-top: ${pxToRem(32)};
`;
